import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { color } from '../../../app/theme';
import { ModalType } from '../../../app/types';
import IconEdit from '../../../assets/images/Icons/IconEdit';
import { openModal } from '../../../features/modalSlice';
import Icons from './Icons';
import Template from './Template';
import TemplateDropdown from './TemplateDropdown';

const useStyles = makeStyles(
  () => ({
    root: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    dropdown: {
      flex: '0 0 auto',
    },
    contentContainer: {
      position: 'relative',
      width: '100%',
    },
    content: {
      cursor: 'pointer',
      width: '100%',
      height: 44,
      fontSize: 15,
      color: color.$3,
      lineHeight: 1,
      borderRadius: 24,
      border: 'solid 1px #bdbdbd',
      backgroundColor: '#ffffff',
      padding: '15px 48px 14px 24px',
      resize: 'none',
      outline: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '&::placeholder': {
        fontSize: 15,
        color: color.$3,
      },
    },
    error: { border: `solid 1px ${color.$17}` },
    icon: {
      cursor: 'pointer',
      position: 'absolute',
      top: 10,
      right: 16,
    },
  }),
  {
    name: 'FileTemplate',
  },
);

function FileTemplate({
  template,
  onChange,
  onChangeType,
  onDelete,
  platformType,
  onUp,
  onDown,
  messageLength,
  isError,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [localTemplate, setLocalTemplate] = useState({
    contentType: 'application/x-image',
    data: {
      name: '',
      contentType: '',
      url: '',
      previewUrl: null,
    },
    index: 0,
  });

  function handleOpenUploadFileDialog() {
    dispatch(
      openModal({
        modalType: ModalType.UPLOAD_DIALOG,
        modalProps: {
          name: localTemplate.data.name,
          contentType: localTemplate.data.contentType,
          url: localTemplate.data.url,
          platformType,
          onUploaded: data => {
            setLocalTemplate({ ...data, index: localTemplate.data.index });
            onChange(data, localTemplate.data.index);
          },
        },
      }),
    );
  }

  useEffect(() => {
    if (localTemplate.data.url !== template.data.url) {
      setLocalTemplate(template);
    }
  }, [template]);

  return (
    <Template>
      <div className={classes.root}>
        <TemplateDropdown
          className={classes.dropdown}
          platformType={platformType}
          messageTemplatetype={template.contentType}
          onChange={onChangeType}
        />
        <Icons
          index={template.index}
          onUp={onUp}
          onDown={onDown}
          onDelete={onDelete}
          messageLength={messageLength}
        />
      </div>
      <div
        className={classes.contentContainer}
        onClick={handleOpenUploadFileDialog}
      >
        <input
          className={classnames(classes.content, { [classes.error]: isError })}
          value={localTemplate.data.url !== '' ? localTemplate.data.name : ''}
          placeholder={t('templates.placeholder-file')}
          readOnly
        />
        <div className={classes.icon}>
          <IconEdit
            className={classes.iconSvg}
            id={`edit-${template.data.index}`}
            color={color.$10}
            hoverColor={color.$10}
          />
        </div>
      </div>
    </Template>
  );
}

FileTemplate.defaultProps = {
  onChange: () => {},
  onChangeType: () => {},
  onDelete: () => {},
  onUp: () => {},
  onDown: () => {},
  messageLength: 0,
};

FileTemplate.propTypes = {
  template: PropTypes.shape({
    data: PropTypes.shape({
      content: PropTypes.string,
    }),
  }).isRequired,
  onChange: PropTypes.func,
  onChangeType: PropTypes.func,
  onDelete: PropTypes.func,
  onUp: PropTypes.func,
  onDown: PropTypes.func,
  messageLength: PropTypes.number,
};

export default FileTemplate;
